import api from './axiosClient'
import { AxiosResponse } from 'axios'
import { GetAllGlobalNotificationResponseProto, GetImageKeyResponseProto, GlobalNotificationProto } from '../../proto/generated/global-notification'
import { GetOrdersPositionAdminResponse, GetTradingAccountsAdminRequest, GetTradingAccountsAdminResponse } from '../../proto/generated/admin.ts'
import {
    ActivateTradingAccountRequest,
    GetDematApplicationsAdminResponse,
    RejectDematApplicationRequest,
    TradingAccount,
    TradingAccountStatus,
    tradingAccountStatusToJSON
} from '../../proto/generated/trading-bridge.ts'
import { PremiumFeatureResponse, SubscriptionFeatureResponse, ManageFeatureRequest, UserSubscriptionPlan, PremiumFeature, SubscriptionFeatures, PremiumFeatureUpsertRequest } from '../../proto/generated/subscription'
import {
    ListPaymentTransactionsAdminResponse,
    PaymentTransaction,
    SimulateTransactionUpdateRequest
} from '../../proto/generated/payments.ts'

export type CacheListResponse = {
    caches: string[]
}

export const fetchCaches = async (): Promise<CacheListResponse> => {
    const response = await api.get<CacheListResponse>(`/admin/job/caches`, {
        headers: {
            Accept: 'application/json'
        }
    })
    return response.data
}

export const handleSubmitCache = async (args: { body: { name: string } }): Promise<AxiosResponse<any, any>> => {
    return api.post(`/admin/job/cache-reload`, args.body, {
        headers: {
            Accept: 'application/json'
        }
    })
}

export const handleSubmitJob = async (args: { body: any }): Promise<AxiosResponse<any, any>>  => {
    return new Promise((resolve, reject) => {
        const url = `/admin/job/job-refresh`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then((response) => {
            resolve(response)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const handleGetAllGlobalNotifications = async (): Promise<GetAllGlobalNotificationResponseProto>  => {
    return new Promise((resolve, reject) => {
        const url = `/admin/global-notification/all`
        api.get(
            url, {
                headers: {
                    'Accept': 'application/x-protobuf'
                },
                responseType: 'arraybuffer'
            }
        ).then((response) => {
            const decodedData = GetAllGlobalNotificationResponseProto.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error);
        })
    })
}

export const handleCreateGlobalNotification = async (args: { body: any }): Promise<AxiosResponse<any, any>>  => {
    return new Promise((resolve, reject) => {
        const url = `/admin/global-notification`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then((response) => {
            resolve(response)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const handleModifyGlobalNotification = async (args: { body: any }): Promise<GlobalNotificationProto> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/global-notification/modify`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then((response) => {
            const decodedData = GlobalNotificationProto.decode(new Uint8Array(response.data));
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const handleDeleteGlobalNotification = async (args: { id: any }): Promise<AxiosResponse<any, any>> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/global-notification/${args.id}`
        api.delete(
            url
        ).then((response) => {
            resolve(response)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const handleUploadImage = async (args: { file: File }): Promise<GetImageKeyResponseProto> => {
    const url = `/admin/global-notification/upload-image`;
    try {
        const response = await api.post(url, args.file, {
            headers: {
                'Content-Type': args.file.type,
                'X-Filename': args.file.name,
                'Accept': 'application/json'
            },
        });
        return response.data
    } catch (error) {
        throw error;
    }
};

export const getUserFromUserId = async (userId: string): Promise<GetTradingAccountsAdminResponse> => {
    const url = `/admin/trading-account`;
    try {
        const body = GetTradingAccountsAdminRequest.create({ userId });
        const response = await api.post(url, body, {
            headers: {
                "Content-Type": "application/x-protobuf",
                Accept: "application/x-protobuf",
            },
            responseType: "arraybuffer",
        });
        return GetTradingAccountsAdminResponse.decode(new Uint8Array(response.data));
    } catch (error) {
        return Promise.reject(error);
    }
};

/**
 * Fetch user by email
 */
export const getUserFromEmail = async (email: string): Promise<GetTradingAccountsAdminResponse> => {
    const url = `/admin/trading-account`;
    try {
        const body = GetTradingAccountsAdminRequest.create({ email });
        const response = await api.post(url, body, {
            headers: {
                "Content-Type": "application/x-protobuf",
                Accept: "application/x-protobuf",
            },
            responseType: "arraybuffer",
        });
        return GetTradingAccountsAdminResponse.decode(new Uint8Array(response.data));
    } catch (error) {
        return Promise.reject(error);
    }
};

/**
 * Fetch users + trading accounts by tradingAccountId
 */
export const getUsersFromTradingAccount = async (tradingAccountId: string): Promise<GetTradingAccountsAdminResponse> => {
    const url = `/admin/trading-account`;
    try {
        const body = GetTradingAccountsAdminRequest.create({ tradingAccountId });
        const response = await api.post(url, body, {
            headers: {
                "Content-Type": "application/x-protobuf",
                Accept: "application/x-protobuf",
            },
            responseType: "arraybuffer",
        });
        return GetTradingAccountsAdminResponse.decode(new Uint8Array(response.data));
    } catch (error) {
        return Promise.reject(error);
    }
};

export const fetchOrdersAndPositions = async (args: { tradingAccountId: string }): Promise<GetOrdersPositionAdminResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/trading-account/orders`
        api.get(url, {
            params: {
                tradingAccountId: args.tradingAccountId
            },
            headers: { Accept: 'application/x-protobuf' },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                const decodedData = GetOrdersPositionAdminResponse.decode(new Uint8Array(response.data))
                resolve(decodedData)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const setDebugModeForTradingAccount = async (tradingAccountId: string, debugMode: boolean): Promise<void> => {
    const url = `/admin/trading-account/debug`;
    try {
        await api.get(url, {
            params: {
                tradingAccountId,
                debugMode,
            },
        });
    } catch (error) {
        return Promise.reject(error);
    }
};

export const fetchAllFeatures = async (): Promise<PremiumFeatureResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/subscription-plan/all-features`
        api.get(url, {
            headers: { Accept: 'application/x-protobuf' },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                const decodedData = PremiumFeatureResponse.decode(new Uint8Array(response.data))
                resolve(decodedData)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const getAllUserSubscription = async (email: string): Promise<UserSubscriptionPlan> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/user-subscription`
        api.get(url, {
            params: { email },
            headers: { Accept: 'application/x-protobuf' },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                const decodedData = UserSubscriptionPlan.decode(new Uint8Array(response.data))
                resolve(decodedData)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const setUserSubscriptionPlan = async (email: string, subscriptionPlan: string): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/user-subscription`
        api.post(url, null, {
            params: {
                email,
                subscriptionPlan
            },
            headers: { Accept: 'application/json' }
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const deleteUserSubscriptionPlan = async (email: string, subscriptionPlan: string): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/user-subscription`
        api.delete(url, {
            params: {
                email,
                subscriptionPlan
            },
            headers: { Accept: 'application/json' }
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const addPremiumFeature = async (premiumFeature: PremiumFeatureUpsertRequest): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/feature/add`
        api.post(url, premiumFeature, {
            headers: {
                'Content-Type': 'application/x-protobuf',
                Accept: 'application/json'
            },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const updatePremiumFeature = async (premiumFeature: PremiumFeatureUpsertRequest): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/feature/update`
        api.post(url, premiumFeature, {
            headers: {
                'Content-Type': 'application/x-protobuf',
                Accept: 'application/json'
            },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const deletePremiumFeature = async (featureCode: string): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/feature/delete/${featureCode}`
        api.delete(url, {
            headers: {
                Accept: 'application/json'
            },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const attachFeatureToMultipleSubscriptionPlans = async (subscriptionPlans: string[], featureCode: string): Promise<any> => {
    const url = `/admin/feature/attach`

    const manageRequest = ManageFeatureRequest.create({
        subscriptionPlan: subscriptionPlans,
        featureCode
    })

    return api.post(url, manageRequest, {
        headers: {
            'Content-Type': 'application/x-protobuf',
            Accept: 'application/x-protobuf'
        },
        responseType: 'arraybuffer'
    })
}

export const removeFeatureFromMultipleSubscriptionPlans = async (subscriptionPlans: string[], featureCode: string): Promise<any> => {
    const url = `/admin/feature/remove`

    const manageRequest = ManageFeatureRequest.create({
        subscriptionPlan: subscriptionPlans,
        featureCode
    })

    return api.post(url, manageRequest, {
        headers: {
            'Content-Type': 'application/x-protobuf',
            Accept: 'application/x-protobuf'
        },
        responseType: 'arraybuffer'
    })
}

export const addSubscriptionPlan = async (features: SubscriptionFeatures): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/user-subscription/add`
        api.post(url, features, {
            headers: {
                'Content-Type': 'application/x-protobuf',
                Accept: 'application/json'
            },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const deleteSubscriptionPlan = async (features: SubscriptionFeatures): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/user-subscription/delete`
        api.delete(url, {
            data: features,
            headers: {
                'Content-Type': 'application/x-protobuf',
                Accept: 'application/json'
            },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const editSubscriptionPlan = async (features: SubscriptionFeatures): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/user-subscription/edit`
        api.post(url, features, {
            headers: {
                'Content-Type': 'application/x-protobuf',
                Accept: 'application/json'
            },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const deleteUser = async( userId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/user/delete`
        api.delete(url, {
            data: { userId },
            headers: {
                Accept: 'application/json'
            },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                resolve(response.data)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const handleReloadClientVersion = async (): Promise<AxiosResponse<any, any>> => {
    return new Promise((resolve, reject) => {
        const url = `/admin/job/client-version-policy-reload`
        api.post(url, null, {
            headers: {
                Accept: 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        })
            .then((response) => {
                resolve(response)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

const protobufHeaders = {
    Accept: 'application/x-protobuf'
}

export const getDematApplicationsAdmin = async (args?: {
    status?: TradingAccountStatus
    page?: number
    pageSize?: number
}): Promise<GetDematApplicationsAdminResponse> => {
    const params = new URLSearchParams()
    if (args?.status !== undefined && args.status !== TradingAccountStatus.STATUS_UNKNOWN) {
        params.set('status', tradingAccountStatusToJSON(args.status))
    }
    if (args?.page !== undefined) params.set('page', String(args.page))
    if (args?.pageSize !== undefined) params.set('pageSize', String(args.pageSize))
    const query = params.toString()
    const url = `/admin/trading-account/applications${query ? `?${query}` : ''}`
    const response = await api.get(url, {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return GetDematApplicationsAdminResponse.decode(new Uint8Array(response.data))
}

export const getDematApplicationAdmin = async (accountId: string): Promise<TradingAccount> => {
    const response = await api.get(`/admin/trading-account/applications/${accountId}`, {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return TradingAccount.decode(new Uint8Array(response.data))
}

export const activateDematApplicationAdmin = async (
    accountId: string,
    request: ActivateTradingAccountRequest
): Promise<TradingAccount> => {
    const body = ActivateTradingAccountRequest.create({
        ...request,
        accountId
    })
    const response = await api.post(
        `/admin/trading-account/applications/${accountId}/activate`,
        JSON.stringify(ActivateTradingAccountRequest.toJSON(body)),
        {
            headers: protobufHeaders,
            responseType: 'arraybuffer'
        }
    )
    return TradingAccount.decode(new Uint8Array(response.data))
}

export const rejectDematApplicationAdmin = async (
    accountId: string,
    reason: string
): Promise<void> => {
    const body = RejectDematApplicationRequest.create({ accountId, reason })
    await api.post(
        `/admin/trading-account/applications/${accountId}/reject`,
        JSON.stringify(RejectDematApplicationRequest.toJSON(body)),
        {
            headers: protobufHeaders,
            responseType: 'arraybuffer'
        }
    )
}

export type CorporateActionStatus = 'PENDING' | 'CONFIRMED'
export type CorporateActionSource = 'IIFL' | 'EXCHANGE'
export type CorporateActionMutationOperation = 'ADD' | 'UPDATE' | 'REPLACE' | 'DELETE' | 'CONFIRM'

export const CORPORATE_ACTION_TYPES = [
    'BONUS',
    'SPLIT',
    'RIGHTS',
    'INTERIM_DIVIDEND',
    'FINAL_DIVIDEND',
    'SPECIAL_DIVIDEND'
] as const

export type CorporateActionTypeName = (typeof CORPORATE_ACTION_TYPES)[number]

export interface CorporateActionKey {
    isin: string
    corporateActionType: string
    exDate: string
}

export interface CorporateActionRecord {
    isin: string
    companyName: string
    corporateActionType: string
    exDate: string
    recordDate?: string | null
    announcementDate?: string | null
    dividendPercentage?: number | string | null
    dividendAmount?: number | string | null
    ratioNumerator?: number | string | null
    ratioDenominator?: number | string | null
    newFaceValue?: number | string | null
    rightsPremium?: number | string | null
    previousDayClose?: number | string | null
    message?: string | null
    status: CorporateActionStatus
    source?: CorporateActionSource | null
}

export interface CorporateActionListResponse {
    etag: string | null
    total: number
    page: number
    pageSize: number
    records: CorporateActionRecord[]
}

export interface CorporateActionMutationRequest {
    operation: CorporateActionMutationOperation
    expectedEtag: string
    currentKey?: CorporateActionKey | null
    record?: CorporateActionRecord | null
}

export interface CorporateActionMutationResponse {
    applied: boolean
    operation: CorporateActionMutationOperation
    etag: string | null
    projectionStatus?: string
    record?: CorporateActionRecord | null
    currentKey?: CorporateActionKey | null
    newKey?: CorporateActionKey | null
}

const jsonHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
}

export const listCorporateActionsAdmin = async (args?: {
    from?: string
    to?: string
    page?: number
    pageSize?: number
    status?: CorporateActionStatus
    source?: CorporateActionSource
    corporateActionType?: string
    search?: string
}): Promise<CorporateActionListResponse> => {
    const params = new URLSearchParams()
    if (args?.from) params.set('from', args.from)
    if (args?.to) params.set('to', args.to)
    if (args?.page !== undefined) params.set('page', String(args.page))
    if (args?.pageSize !== undefined) params.set('pageSize', String(args.pageSize))
    if (args?.status) params.set('status', args.status)
    if (args?.source) params.set('source', args.source)
    if (args?.corporateActionType) params.set('corporateActionType', args.corporateActionType)
    if (args?.search) params.set('search', args.search)
    const query = params.toString()
    const url = `/admin/corporate-action${query ? `?${query}` : ''}`
    const response = await api.get(url, { headers: jsonHeaders })
    return response.data
}

export const mutateCorporateActionAdmin = async (
    request: CorporateActionMutationRequest
): Promise<CorporateActionMutationResponse> => {
    const response = await api.post('/admin/corporate-action/mutation', request, {
        headers: jsonHeaders
    })
    return response.data
}

export const listPaymentTransactionsAdmin = async (args?: {
    from?: string
    to?: string
    page?: number
    pageSize?: number
    status?: string
}): Promise<ListPaymentTransactionsAdminResponse> => {
    const params = new URLSearchParams()
    if (args?.from) params.set('from', args.from)
    if (args?.to) params.set('to', args.to)
    if (args?.page !== undefined) params.set('page', String(args.page))
    if (args?.pageSize !== undefined) params.set('pageSize', String(args.pageSize))
    if (args?.status) params.set('status', args.status)
    const query = params.toString()
    const url = `/admin/payments/transactions${query ? `?${query}` : ''}`
    const response = await api.get(url, {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return ListPaymentTransactionsAdminResponse.decode(new Uint8Array(response.data))
}

export const simulatePaymentTransactionAdmin = async (
    transactionId: string,
    request: SimulateTransactionUpdateRequest
): Promise<PaymentTransaction> => {
    const response = await api.post(
        `/admin/payments/transactions/${encodeURIComponent(transactionId)}/simulate`,
        JSON.stringify(SimulateTransactionUpdateRequest.toJSON(request)),
        {
            headers: protobufHeaders,
            responseType: 'arraybuffer'
        }
    )
    return PaymentTransaction.decode(new Uint8Array(response.data))
}
